import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription, firstValueFrom, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthService,
  LoginResponse,
  AuthToken,
  ValidOTPResponse,
  UserCompany,
} from 'src/app/services/auth.service';
import { Utils } from 'src/app/services/utils';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { environment } from 'src/environments/environment';
import {
  logGoogleFlow,
  logGoogleFlowRedirect,
} from 'src/app/utils/google-auth-flow-log';
import { isSocialSignInEnabled, useGoogleEmailOtpIdp, useFirebasePasswordlessEmail, useNumericGoogleEmailOtp } from '../../config/auth-config';
import {
  resolveAyaDeployTier,
  wwwAppUrlForTier,
} from 'src/app/utils/aya-deploy-urls';
import { resolveFinalLogoutLandingUrl } from 'src/app/utils/cross-app-logout';
import { AvailableApplication } from 'src/app/services/environment.interface';

type AuthMode = 'login' | 'signup';
type ModalStep =
  | 'email'
  | 'password'
  | 'code'
  | 'phone-register'
  | 'mfa-code'
  | 'email-link-confirm'
  | 'none';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild('loginForm') loginForm: NgForm | undefined = undefined;
  secondsCountdown = 0;
  countdownSubscription: Subscription;
  email = '';
  password = '';
  otp = '';
  phoneNumber = '';
  inProgress = false;
  error = '';
  geofenceBlocked = false;
  authMode: AuthMode = 'login';
  modalStep: ModalStep = 'none';
  isLoggedIn = false;
  /** Blank page while sending an already signed-in user back to Lovable. */
  isRedirecting = false;
  displayName = '';
  sessionReady = false;
  /** True while deep-link social redirect UI is active. */
  isDeepLinkPending = false;
  /** Lovable ?email= flow (OTP send may still be in progress on inbox page). */
  emailDeepLinkActive = false;
  /** Lovable deep-link social provider awaiting one user click. */
  deepLinkSocialProvider: 'google' | 'apple' | null = null;
  partialPhone = '';
  year = new Date().getFullYear();
  authType: 'Authenticator' | 'SMS' | 'Password' | 'Completed' = 'Authenticator';
  smsMfaEnabled: boolean | null = null;
  smsMfaStatusMessage = '';
  smsMfaActionInProgress = false;
  smsMfaStatusLoading = false;
  /** User is registering phone + SMS code to turn on SMS 2FA from profile. */
  enablingSmsMfa = false;
  /** Group membership was added; roll back on cancel if user abandons setup. */
  smsMfaPrepareApplied = false;
  /** Driven by `environment.emailOtpIdp` — google enables social buttons; entra disables them. */
  readonly socialSignInEnabled = isSocialSignInEnabled();
  /** Firebase passwordless email link instead of numeric OTP (ACS / Twilio Verify). */
  readonly firebasePasswordlessEmail = useFirebasePasswordlessEmail();
  /** Primary delivery is numeric OTP; user may opt into email link on the inbox screen. */
  readonly numericGoogleEmailOtp = useNumericGoogleEmailOtp();
  /** User switched from OTP to passwordless email link on the inbox screen. */
  passwordlessEmailLinkMode = false;
  /** Avoid wiping storage more than once per page load when the login modal opens. */
  private guestBrowserStorageCleared = false;

  get showPasswordlessInboxUi(): boolean {
    return this.firebasePasswordlessEmail || this.passwordlessEmailLinkMode;
  }

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _authService: AuthService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.countdownSubscription = new Subscription();
    if (history?.state?.from === 'verify') {
      history.replaceState({}, '');
    }
  }

  async ngOnInit() {
    this._authService.setUserIpAddress();
    if (!(await this._authService.probeGeofenceAccess())) {
      this.showGeofenceBlocked();
      return;
    }
    this.capturePostAuthAppFromRoute();
    const appFromRoute = String(
      this._route.snapshot.queryParams['app'] || ''
    ).trim();
    this._authService.prepareProductAppLoginEntry(appFromRoute);

    const loggedOutParam =
      String(this._route.snapshot.queryParams['loggedOut'] || '') === '1';
    if (loggedOutParam) {
      await this.handleSignedOutLandingPage();
      return;
    }

    const emailDeepLinkForSignedOutGate =
      this.getEmailDeepLinkFromRoute() ||
      this._authService.peekEmailDeepLinkIntent()?.email ||
      '';
    const providerDeepLinkEarly = this.getDeepLinkProvider();
    const skipSignedOutWwwBounce =
      (Utils.isValidEmail(emailDeepLinkForSignedOutGate) &&
        !this.isSmsMfaManageDeepLink()) ||
      !!providerDeepLinkEarly;

    if (
      this._authService.wasSignedOutLocally() &&
      !this._authService.redirectsWithoutProductLicense(appFromRoute) &&
      !skipSignedOutWwwBounce
    ) {
      const wwwLanding = this.resolveSignedOutWwwLanding();
      if (wwwLanding) {
        window.location.replace(wwwLanding);
        return;
      }
      this.presentSignedOutLoginShell();
      return;
    }

    const providerDeepLink = this.getDeepLinkProvider();
    const returningFromSocialOAuth =
      this._authService.isReturningFromFirebaseSocialRedirect() ||
      this._authService.peekSocialOAuthReturnFlag();
    const pendingSocialBeforeClear =
      this._authService.hasPendingSocialRedirect();
    if (
      providerDeepLink &&
      !this.isOAuthRedirectCallback() &&
      !returningFromSocialOAuth &&
      !pendingSocialBeforeClear
    ) {
      logGoogleFlow('login:sessionStorage.clear before ?provider= deep-link', {
        provider: providerDeepLink,
      });
      this._authService.clearSessionStorageForNewSocialLogin();
    }

    if (!this._authService.redirectsWithoutProductLicense(appFromRoute)) {
      this.captureDeepLinkReturnUrl();
      this.captureDeepLinkSignupSource();
    }

    let deepLinkEmail = this.getEmailDeepLinkFromRoute();
    const pendingEmailIntent = this._authService.peekEmailDeepLinkIntent();
    if (!deepLinkEmail && pendingEmailIntent?.email) {
      deepLinkEmail = pendingEmailIntent.email;
      if (
        pendingEmailIntent.returnUrl &&
        !this._authService.redirectsWithoutProductLicense(appFromRoute)
      ) {
        this._authService.captureDeepLinkReturnUrl(pendingEmailIntent.returnUrl);
      }
      if (pendingEmailIntent.signupSource) {
        this._authService.captureDeepLinkSignupSource(
          pendingEmailIntent.signupSource
        );
      }
    }
    const hasEmailDeepLink = Utils.isValidEmail(deepLinkEmail);
    /** Lovable-style ?email= — always re-authenticate; do not bounce with stale session. */
    const forceEmailDeepLinkLogin =
      hasEmailDeepLink && !this.isSmsMfaManageDeepLink();

    let redirectError: string | null = null;
    if (forceEmailDeepLinkLogin) {
      this._authService.consumeEntraRedirectError();
    } else {
      redirectError = this._authService.consumeEntraRedirectError();
    }
    if (redirectError) {
      this.error = redirectError;
      this.sessionReady = true;
      this.openModal('login');
      this._cdr.detectChanges();
    }

    if (useGoogleEmailOtpIdp()) {
      try {
        const verified = await this._authService.tryApplyFirebaseEmailActionFromUrl();
        if (verified) {
          this.error = '';
          this.sessionReady = true;
          this.openModal('login');
          this._cdr.detectChanges();
          return;
        }
      } catch (err: unknown) {
        this.applyAuthApiError(err);
        this.sessionReady = true;
        this.openModal('login');
        this._cdr.detectChanges();
        return;
      }
    }

    if (useGoogleEmailOtpIdp()) {
      const isEmailLinkReturn =
        this._authService.isFirebaseEmailLinkSignInReturn();
      if (
        isEmailLinkReturn &&
        !this._authService.hasResolvedEmailForSignInLink()
      ) {
        this.email = '';
        this.error = '';
        this.sessionReady = true;
        this.modalStep = 'email-link-confirm';
        this._cdr.detectChanges();
        return;
      }

      try {
        const linked = await this._authService.tryCompleteFirebaseEmailLinkSignIn();
        if (linked?.status === 'Token') {
          this.handleEntraAuthResult(linked);
          return;
        }
      } catch (err: unknown) {
        if (isEmailLinkReturn) {
          this.email = this._authService.resolveEmailForSignInLink();
          this.applyAuthApiError(err);
          this.sessionReady = true;
          this.modalStep = 'email-link-confirm';
          this._cdr.detectChanges();
          return;
        }
        this.applyAuthApiError(err);
        this.sessionReady = true;
        this.openModal('login');
        this._cdr.detectChanges();
      }
    }

    if (forceEmailDeepLinkLogin) {
      this._authService.stripOAuthParamsForEmailDeepLink();
    }

    let deepLinkProvider = this.getDeepLinkProvider();
    let pendingSocial = this._authService.hasPendingSocialRedirect();
    let oauthReturn = this.isOAuthRedirectCallback();
    const firebaseRedirectReturn =
      this._authService.isFirebaseSocialRedirectReturn();
    const firebaseOAuthUserPending =
      this.socialSignInEnabled &&
      this._authService.isReturningFromFirebaseSocialRedirect();
    const socialOAuthAborted =
      this._authService.consumeSocialOAuthAbortedFlag();
    const hadSocialOAuthReturn =
      this._authService.consumeSocialOAuthReturnFlag();

    logGoogleFlow('login:ngOnInit snapshot', {
      deepLinkProvider,
      pendingSocial,
      oauthReturn,
      firebaseRedirectReturn,
      firebaseOAuthUserPending,
      socialOAuthAborted,
      hadSocialOAuthReturn,
      queryParams: { ...this._route.snapshot.queryParams },
      deepLinkReturnUrl: this._authService.deepLinkReturnUrl,
    });

    if (
      this._authService.peekOAuthFailureInBrowser() ||
      this._route.snapshot.queryParams['error']
    ) {
      logGoogleFlow('login:path=oauthQueryError → modal');
      this._authService.markSocialOAuthAborted();
      this._authService.clearStaleSocialRedirectPending();
      this._authService.consumeSocialDeepLink();
      this.error =
        this._authService.consumeEntraRedirectError() ||
        String(this._route.snapshot.queryParams['error_description'] || '') ||
        'Sign-in was cancelled.';
      this._authService.stripOAuthParamsForEmailDeepLink();
      this.sessionReady = true;
      this.openModal('login');
      this._cdr.detectChanges();
      return;
    }

    // ?email= from Lovable: revoke Entra + wipe local/MSAL before any other login logic.
    if (forceEmailDeepLinkLogin && !deepLinkProvider) {
      this.beginEmailDeepLinkLoader(deepLinkEmail);
      this.captureDeepLinkReturnUrl();
      this.captureDeepLinkSignupSource();
      await this._authService.prepareForEmailDeepLink(
        deepLinkEmail,
        this._route.snapshot.queryParams['returnUrl']
      );
      pendingSocial = this._authService.hasPendingSocialRedirect();
      oauthReturn = this.isOAuthRedirectCallback();
    }

    // Returning from Entra loginRedirect — finish before ?email= deep-link clears MSAL.
    const skipStaleSocialForEmailDeepLink =
      forceEmailDeepLinkLogin && !oauthReturn && pendingSocial;
    /** Fresh Lovable ?provider= without OAuth params — do not run stale pending completion. */
    const skipStaleSocialForProviderDeepLink =
      !!deepLinkProvider &&
      !oauthReturn &&
      !hadSocialOAuthReturn &&
      !firebaseRedirectReturn &&
      !firebaseOAuthUserPending &&
      pendingSocial;
    if (
      !redirectError &&
      (pendingSocial ||
        oauthReturn ||
        hadSocialOAuthReturn ||
        firebaseRedirectReturn ||
        firebaseOAuthUserPending) &&
      !skipStaleSocialForEmailDeepLink &&
      !skipStaleSocialForProviderDeepLink &&
      !socialOAuthAborted
    ) {
      logGoogleFlow('login:path=tryCompleteSocialRedirect (pending/oauthReturn/hadReturn)');
      this.captureDeepLinkReturnUrl();
      this.beginSocialOAuthLoader(
        deepLinkProvider ||
          this._authService.peekSocialDeepLink().provider ||
          'google'
      );
      this._cdr.detectChanges();
      if (await this.tryCompleteSocialRedirect()) {
        logGoogleFlow('login:tryCompleteSocialRedirect finished (handled)');
        return;
      }
      logGoogleFlow('login:tryCompleteSocialRedirect returned false');

      if (
        pendingSocial ||
        oauthReturn ||
        hadSocialOAuthReturn ||
        firebaseRedirectReturn ||
        firebaseOAuthUserPending
      ) {
        const stale = this._authService.peekSocialDeepLink();
        if (stale.returnUrl) {
          this._authService.captureDeepLinkReturnUrl(stale.returnUrl);
        }
        this._authService.clearStaleSocialRedirectPending();
        this.error =
          this._authService.consumeEntraRedirectError() ||
          (oauthReturn || hadSocialOAuthReturn
            ? this.socialSignInEnabled
              ? 'Google sign-in did not finish after returning from the provider. Use Continue with Google to try again.'
              : 'Google sign-in did not finish after returning from Microsoft. Use Continue with Google to try again.'
            : 'Google sign-in was interrupted. Use Continue with Google to try again.');
        this.sessionReady = true;
        this.isDeepLinkPending = false;
        this.isRedirecting = false;
        this.inProgress = false;
        this.openModal('login');
        this._cdr.detectChanges();
        return;
      }
    }

    if (socialOAuthAborted) {
      logGoogleFlow('login:path=socialOAuthAborted → modal');
      this.error =
        redirectError ||
        this._authService.consumeEntraRedirectError() ||
        'Sign-in was cancelled.';
      this.sessionReady = true;
      this.isDeepLinkPending = false;
      this.isRedirecting = false;
      this.inProgress = false;
      this.openModal('login');
      this._cdr.detectChanges();
      return;
    }

    if (
      hadSocialOAuthReturn &&
      !this._authService.hasPendingSocialRedirect()
    ) {
      logGoogleFlow('login:path=hadSocialOAuthReturn without pending');
      this.beginSocialOAuthLoader(
        deepLinkProvider ||
          this._authService.peekSocialDeepLink().provider ||
          'google'
      );
      this._cdr.detectChanges();
      if (await this.tryCompleteSocialRedirect()) {
        logGoogleFlow('login:recovered social complete after missing pending');
        return;
      }
      this.error =
        this._authService.consumeEntraRedirectError() ||
        'Google sign-in did not finish. Please try again.';
      this.sessionReady = true;
      this.isDeepLinkPending = false;
      this.isRedirecting = false;
      this.inProgress = false;
      this.openModal('login');
      this._cdr.detectChanges();
      return;
    }

    // Fresh Lovable ?provider= — auto-start Google/Apple (not a return from OAuth).
    if (
      deepLinkProvider &&
      !hadSocialOAuthReturn &&
      !firebaseOAuthUserPending
    ) {
      if (!isSocialSignInEnabled()) {
        logGoogleFlow('login:socialSignInDisabled → email modal');
        this.captureDeepLinkReturnUrl();
        this.sessionReady = true;
        this.openModal('login');
        await this._router.navigate([], {
          relativeTo: this._route,
          replaceUrl: true,
          queryParams: { provider: null },
          queryParamsHandling: 'merge',
        });
        this._cdr.detectChanges();
        return;
      }
      if (pendingSocial && !oauthReturn) {
        logGoogleFlow('login:clear stale pending before provider deep-link');
        this._authService.clearStaleSocialRedirectPending();
      }
      if (!this._authService.canAutoStartSocialRedirect()) {
        logGoogleFlow('login:path=socialRedirectLoopGuard → modal');
        this.captureDeepLinkReturnUrl();
        this.error =
          'Google sign-in is restarting too often. Wait a minute, then use Continue with Google again.';
        this.sessionReady = true;
        this.openModal('login');
        this._cdr.detectChanges();
        return;
      }
      logGoogleFlow('login:path=autoStartSocialDeepLink', {
        provider: deepLinkProvider,
      });
      await this._authService.prepareForSocialDeepLink();
      this.captureDeepLinkReturnUrl();
      this.sessionReady = true;
      await this.tryDeepLinkSocialLogin(deepLinkProvider);
      this._cdr.detectChanges();
      return;
    }

    // Lovable ?email=: resolve-email → Google/Apple/email OTP (after session teardown).
    if (forceEmailDeepLinkLogin) {
      this.captureDeepLinkReturnUrl();
      if (deepLinkEmail && !this.getEmailDeepLinkFromRoute()) {
        const queryParams: Record<string, string> = { email: deepLinkEmail };
        const returnUrl =
          this._authService.deepLinkReturnUrl ||
          pendingEmailIntent?.returnUrl ||
          null;
        if (returnUrl) {
          queryParams['returnUrl'] = returnUrl;
        }
        await this._router.navigate(['/login'], {
          replaceUrl: true,
          queryParams,
        });
      }
      this.sessionReady = true;
      await this.startEmailDeepLinkLoginFromRoute(deepLinkEmail);
      this._cdr.detectChanges();
      return;
    }

    const handoffFailed =
      String(this._route.snapshot.queryParams['handoffFailed'] || '').trim() ===
      '1';
    if (handoffFailed) {
      this.capturePostAuthAppFromRoute();
      this.sessionReady = true;
      this.openModal('login');
      this._cdr.detectChanges();
      return;
    }

    // Bounce to Lovable only when there is no ?email= deep-link (or SMS MFA manage link).
    const completedSessionEmail = this._authService.storedUserName;
    if (
      completedSessionEmail &&
      !forceEmailDeepLinkLogin &&
      !this._authService.wasSignedOutLocally()
    ) {
      await this._authService.ensureAccountsSessionFromEntra();
      this.email = completedSessionEmail;
      this.modalStep = 'none';
      this.sessionReady = true;

      if (this.isSmsMfaManageDeepLink()) {
        this.isLoggedIn = true;
        await this.loadDisplayName(completedSessionEmail);
        this._cdr.detectChanges();
        this.loadSmsMfaStatus();
        return;
      }

      this.isRedirecting = true;
      this._cdr.detectChanges();
      await this.redirectLoggedInUser();
      return;
    }

    this.sessionReady = true;

    if (hasEmailDeepLink && !oauthReturn) {
      if (await this.startEmailDeepLinkLoginFromRoute(deepLinkEmail)) {
        this._cdr.detectChanges();
        return;
      }
    }

    if (this.shouldDelegateLoginToHealthChat()) {
      this.redirectToHealthChatLogin();
      return;
    }

    this.openModal('login');
    this._cdr.detectChanges();
  }

  openModal(mode: AuthMode = 'login') {
    if (this.isLoggedIn) {
      return;
    }
    this.clearGuestBrowserStorageIfNeeded();
    this.authMode = mode;
    this.error = '';
    this.password = '';
    this.otp = '';
    this.secondsCountdown = 0;
    this.modalStep = 'email';
  }

  /**
   * Fresh visit to dev-auth: wipe stale tokens from other apps before login modal.
   * Skipped while finishing OAuth / email-link / in-progress login steps.
   */
  private clearGuestBrowserStorageIfNeeded(): void {
    if (this.guestBrowserStorageCleared || !this.shouldClearStorageForLoginModal()) {
      return;
    }
    this._authService.clearAllBrowserStorageForGuestLogin();
    this.guestBrowserStorageCleared = true;
  }

  private shouldClearStorageForLoginModal(): boolean {
    if (this.isRedirecting || this.isDeepLinkPending || this.emailDeepLinkActive) {
      return false;
    }
    if (
      this.modalStep !== 'none' &&
      this.modalStep !== 'email' &&
      this.modalStep !== 'password'
    ) {
      return false;
    }
    if (this._authService.isReturningFromFirebaseSocialRedirect()) {
      return false;
    }
    if (this._authService.hasPendingSocialRedirect()) {
      return false;
    }
    if (this._authService.peekSocialOAuthReturnFlag()) {
      return false;
    }
    if (this._authService.isFirebaseEmailLinkSignInReturn()) {
      return false;
    }
    if (this.isOAuthRedirectCallback()) {
      return false;
    }
    return true;
  }

  private resetGuestUi(): void {
    this.isLoggedIn = false;
    this.isRedirecting = false;
    this.isDeepLinkPending = false;
    this.emailDeepLinkActive = false;
    this.displayName = '';
    this.email = '';
    this.password = '';
    this.otp = '';
    this.authType = 'Authenticator';
    this.inProgress = false;
    this.sessionReady = true;
    this.authMode = 'login';
    this.modalStep = 'email';
  }

  async logout() {
    this.error = '';
    this.inProgress = true;
    this._cdr.detectChanges();

    try {
      await this._authService.logoutAndRedirect({
        everywhere: true,
      });
    } catch (err: any) {
      this.error =
        err?.error?.message ||
        err?.message ||
        'Sign out failed. Please try again.';
      this.inProgress = false;
      this._cdr.detectChanges();
    }
  }

  private async loadDisplayName(fallbackEmail: string): Promise<void> {
    this.displayName = await this._authService.resolveUserDisplayName(
      fallbackEmail
    );
    this._cdr.detectChanges();
  }

  /** Persist optional returnUrl from the query string for post-auth redirect. */
  private captureDeepLinkReturnUrl() {
    const raw = this._route.snapshot.queryParams['returnUrl'];
    logGoogleFlow('login:captureDeepLinkReturnUrl', {
      rawReturnUrl: raw ? String(raw) : null,
      stored: this._authService.deepLinkReturnUrl,
    });
    this._authService.captureDeepLinkReturnUrl(raw);
  }

  /** Persist optional signupSource from the query string for first-time registration. */
  private captureDeepLinkSignupSource() {
    const raw = this._route.snapshot.queryParams['signupSource'];
    this._authService.captureDeepLinkSignupSource(raw);
  }

  private capturePostAuthAppFromRoute(): void {
    this._authService.capturePostAuthApp(
      this._route.snapshot.queryParams['app']
    );
  }

  /** ?app= from route; session fallback when OAuth or product-app login stripped query params. */
  private resolvePostAuthAppQuery(): string | null {
    const fromRoute = String(this._route.snapshot.queryParams['app'] || '')
      .trim()
      .toLowerCase();
    if (fromRoute) {
      this._authService.capturePostAuthApp(fromRoute);
      return fromRoute;
    }
    if (this.shouldRestorePostAuthAppFromSession()) {
      return this._authService.resolvePostAuthApp();
    }
    const stored = this._authService.resolvePostAuthApp();
    if (this._authService.redirectsWithoutProductLicense(stored)) {
      return stored;
    }
    this._authService.consumePostAuthApp();
    return null;
  }

  private shouldRestorePostAuthAppFromSession(): boolean {
    return (
      this.isOAuthRedirectCallback() ||
      this._authService.isReturningFromFirebaseSocialRedirect() ||
      this._authService.hasPendingSocialRedirect() ||
      this._authService.peekSocialOAuthReturnFlag()
    );
  }

  /** returnUrl from memory, query, or session — validated (localhost allowed in local builds). */
  private resolvePostAuthReturnUrl(): string | null {
    if (this._authService.deepLinkReturnUrl) {
      return this._authService.deepLinkReturnUrl;
    }
    const fromQuery = this._route.snapshot.queryParams['returnUrl'];
    if (fromQuery) {
      const sanitized = Utils.sanitizeReturnUrl(String(fromQuery).trim());
      if (sanitized) {
        return sanitized;
      }
    }
    try {
      const fromSession = sessionStorage.getItem('he_auth_return_url');
      if (fromSession) {
        return Utils.sanitizeReturnUrl(fromSession.trim());
      }
    } catch {
      // ignore
    }
    return null;
  }

  private getEmailDeepLinkFromRoute(): string {
    return (this._route.snapshot.queryParams['email'] || '')
      .toString()
      .trim();
  }

  /** Entra (?code=) or Firebase (#apiKey= / authUser) OAuth return on /login or /. */
  private isOAuthRedirectCallback(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (this._authService.isFirebaseSocialRedirectReturn()) {
      return true;
    }
    const q = this._route.snapshot.queryParams;
    if (q['code'] || q['error'] || q['state']) {
      return true;
    }
    const hash = window.location.hash || '';
    return /(^|[?&])(code|error|state)=/i.test(hash);
  }

  /** Lovable / deep-link: stay on account portal to manage SMS 2FA, then return. */
  isSmsMfaManageDeepLink(): boolean {
    const manage = (this._route.snapshot.queryParams['manage'] || '')
      .toString()
      .trim()
      .toLowerCase();
    return manage === 'sms-mfa' || manage === 'sms_mfa' || manage === '2fa';
  }

  /** Signed-in profile UI is only for SMS 2FA management deep-links. */
  get showSignedInProfile(): boolean {
    return this.isLoggedIn && this.isSmsMfaManageDeepLink() && !this.enablingSmsMfa;
  }

  private async redirectLoggedInUser(): Promise<void> {
    const appQueryParam = this.resolvePostAuthAppQuery();
    if (
      this._authService.wasSignedOutLocally() &&
      !this._authService.redirectsWithoutProductLicense(appQueryParam)
    ) {
      return;
    }
    if (this._authService.redirectsWithoutProductLicense(appQueryParam)) {
      this._authService.prepareProductAppLoginEntry(appQueryParam);
    } else if (this._authService.deepLinkReturnUrl) {
      const returnUrl = this._authService.deepLinkReturnUrl;
      this._authService.deepLinkReturnUrl = null;
      await this._authService.redirectToUrlWithTokenPrepared(returnUrl);
      return;
    }

    const authData = this._localStorageService.authorizationData;
    if (appQueryParam && authData?.token) {
      const token: AuthToken = {
        accessToken: authData.token,
        refreshToken: authData.refreshToken || '',
      };
      try {
        const availableProducts = await firstValueFrom(
          this._authService.getProductNamesWithLicenses()
        );
        this._authService.appBasedRedirection(
          availableProducts,
          token,
          appQueryParam
        );
      } catch {
        if (this._authService.tryPostAuthAppFallback(token, appQueryParam)) {
          return;
        }
        this._authService.redirectToAyaApp();
      }
      return;
    }

    this._authService.redirectToAyaApp();
  }

  get deepLinkReturnUrl(): string | null {
    return this._authService.deepLinkReturnUrl;
  }

  returnToDeepLinkApp() {
    const returnUrl = this._authService.deepLinkReturnUrl;
    if (!returnUrl) {
      return;
    }
    this._authService.deepLinkReturnUrl = null;
    void this._authService.redirectToUrlWithTokenPrepared(returnUrl);
  }

  private getDeepLinkProvider(): 'google' | 'apple' | null {
    const raw = (this._route.snapshot.queryParams['provider'] || '')
      .toString()
      .trim()
      .toLowerCase();
    if (raw === 'google' || raw === 'apple') {
      return raw;
    }
    return null;
  }

  /** Post cross-app logout — tear down sessions before any Firebase/MSAL auto-login. */
  private async handleSignedOutLandingPage(): Promise<void> {
    const postAuthApp = String(
      this._route.snapshot.queryParams['app'] || ''
    ).trim();
    await this._authService.finalizeGuestSession();
    this.guestBrowserStorageCleared = true;
    if (postAuthApp) {
      this._authService.prepareProductAppLoginEntry(postAuthApp);
    }
    this.resetGuestUi();
    this.replaceUrlAfterLogout(postAuthApp);
    this.sessionReady = true;
    this.openModal('login');
    this._cdr.detectChanges();
  }

  /** Stay on dev-app login until the user explicitly signs in again. */
  private presentSignedOutLoginShell(): void {
    this.resetGuestUi();
    this.sessionReady = true;
    this.openModal('login');
    this._cdr.detectChanges();
  }

  /** After cross-app logout, finish on aya-health-chat unless ?app=admin|clinic. */
  private resolveSignedOutWwwLanding(): string | null {
    const appFromRoute = String(
      this._route.snapshot.queryParams['app'] || ''
    ).trim();
    if (this._authService.redirectsWithoutProductLicense(appFromRoute)) {
      return null;
    }

    const rawReturnUrl = this._route.snapshot.queryParams['returnUrl'];
    const chain =
      typeof rawReturnUrl === 'string' ? rawReturnUrl.trim() : '';

    if (chain) {
      const sanitizedChain = Utils.sanitizeReturnUrl(chain);
      // Post-login handoff targets (aya) are not logout landing pages.
      if (sanitizedChain && Utils.isAyaAppHandoffUrl(sanitizedChain)) {
        return null;
      }

      const finalLanding = resolveFinalLogoutLandingUrl(chain);
      const sanitizedFinal = finalLanding
        ? Utils.sanitizeReturnUrl(finalLanding)
        : null;
      if (sanitizedFinal && !Utils.isAyaAppHandoffUrl(sanitizedFinal)) {
        try {
          const url = new URL(sanitizedFinal);
          url.searchParams.set('loggedOut', '1');
          return url.href;
        } catch {
          return sanitizedFinal;
        }
      }

      if (Utils.isAyaAppHandoffUrl(chain)) {
        try {
          const url = new URL(this.healthChatAppUrl());
          url.searchParams.set('loggedOut', '1');
          return url.href;
        } catch {
          return this.healthChatAppUrl();
        }
      }
    }

    try {
      const url = new URL(this.healthChatAppUrl());
      url.searchParams.set('loggedOut', '1');
      return url.href;
    } catch {
      return null;
    }
  }

  private replaceUrlAfterLogout(preserveApp?: string): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('loggedOut');
      url.searchParams.delete('token');
      url.searchParams.delete('refreshToken');
      url.searchParams.delete('email');
      url.searchParams.delete('returnUrl');
      if (preserveApp) {
        url.searchParams.set('app', preserveApp);
      }
      const next = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState({}, '', next);
    } catch {
      // ignore
    }
  }

  /** After /redirect or OAuth cancel — stay on auth portal with message (do not dump to chat). */
  private finishSocialOAuthReturnWithoutSession(
    redirectErrorHint: string | null
  ): void {
    this._authService.consumeSocialDeepLink();
    this._authService.clearStaleSocialRedirectPending();
    this.error =
      redirectErrorHint ||
      this._authService.consumeEntraRedirectError() ||
      'Sign-in was cancelled.';
    this.sessionReady = true;
    this.isDeepLinkPending = false;
    this.isRedirecting = false;
    this.inProgress = false;
    this.openModal('login');
    this._cdr.detectChanges();
  }

  private healthChatAppUrl(): string {
    const returnUrl = this.resolvePostAuthReturnUrl();
    const tier = resolveAyaDeployTier(returnUrl);
    if (tier === 'qa') {
      return (
        Utils.sanitizeReturnUrl(wwwAppUrlForTier('qa')) ||
        'https://qa-www.healthexam.ai/'
      );
    }
    return (
      Utils.sanitizeReturnUrl(environment.ayaHealthChatAppUrl) ||
      'https://dev-www.healthexam.com/'
    );
  }

  /**
   * Aya (8080) sent the user here for a session check — no auth session and no
   * active login intent; show the www login entry instead of auth UI.
   */
  private shouldDelegateLoginToHealthChat(): boolean {
    const appFromRoute = String(
      this._route.snapshot.queryParams['app'] || ''
    ).trim();
    if (this._authService.redirectsWithoutProductLicense(appFromRoute)) {
      return false;
    }
    if (this.getDeepLinkProvider()) {
      return false;
    }
    if (this.getDeepLinkProvider()) {
      return false;
    }
    if (this._authService.hasPendingSocialRedirect()) {
      return false;
    }
    if (Utils.isValidEmail(this.getEmailDeepLinkFromRoute())) {
      return false;
    }
    if (this.isOAuthRedirectCallback()) {
      return false;
    }
    if (this._authService.isFirebaseSocialRedirectReturn()) {
      return false;
    }
    if (this._authService.isReturningFromFirebaseSocialRedirect()) {
      return false;
    }
    if (this._authService.hasPendingSocialRedirect()) {
      return false;
    }
    if (this.isSmsMfaManageDeepLink()) {
      return false;
    }
    if (String(this._route.snapshot.queryParams['loggedOut'] || '') === '1') {
      return false;
    }
    if (
      useGoogleEmailOtpIdp() &&
      this._authService.isFirebaseEmailLinkSignInReturn()
    ) {
      return false;
    }

    const returnUrl = this.resolvePostAuthReturnUrl();
    return !!(returnUrl && Utils.isAyaAppHandoffUrl(returnUrl));
  }

  private redirectToHealthChatLogin(): void {
    const returnUrl =
      this.resolvePostAuthReturnUrl() ||
      Utils.sanitizeReturnUrl(environment.ayaAppUrl);
    const url = new URL(this.healthChatAppUrl());
    if (returnUrl) {
      url.searchParams.set('returnUrl', returnUrl);
    }
    window.location.replace(url.toString());
  }

  /** Close login popup and return to Aya Health Chat (8081 / Lovable chat). */
  dismissAuthToHealthChat(): void {
    window.location.href = this.healthChatAppUrl();
  }

  /**
   * Deep-link social login (Lovable):
   *   /login?provider=google&returnUrl=...
   */
  private async tryDeepLinkSocialLogin(
    provider: 'google' | 'apple'
  ): Promise<void> {
    await this.startFullPageSocialSignIn(provider, { deepLinkUi: true });
  }

  /**
   * Google / Apple via full-page loginRedirect — navigates to IdP (no popup).
   */
  private async startFullPageSocialSignIn(
    provider: 'google' | 'apple',
    options?: { deepLinkUi?: boolean }
  ): Promise<void> {
    this.beginSocialOAuthLoader(provider);
    this._cdr.detectChanges();

    try {
      if (!this.socialSignInEnabled) {
        await this._authService.ensureMsalReady();
      }
      const returnUrl =
        this._authService.deepLinkReturnUrl ||
        this._route.snapshot.queryParams['returnUrl'] ||
        null;
      const postAuthApp = this.resolvePostAuthAppQuery();
      logGoogleFlow('login:startFullPageSocialSignIn', {
        provider,
        deepLinkUi: options?.deepLinkUi === true,
        returnUrl,
        firebase: this.socialSignInEnabled,
      });
      if (returnUrl) {
        this._authService.captureDeepLinkReturnUrl(String(returnUrl));
      }
      await this._authService.clearAllBrowserStorageBeforeSocialRedirect(
        provider,
      );
      this.guestBrowserStorageCleared = true;
      this._authService.persistSocialDeepLink(
        returnUrl ? String(returnUrl) : null,
        provider,
        postAuthApp
      );
      this._authService.recordSocialRedirectAttempt();
      await firstValueFrom(this._authService.startSocialRedirect(provider));
      // Drop ?provider= only after redirect starts so a refresh mid-flow can retry.
      if (this.getDeepLinkProvider()) {
        await this._router.navigate([], {
          relativeTo: this._route,
          replaceUrl: true,
          queryParams: { provider: null },
          queryParamsHandling: 'merge',
        });
      }
      logGoogleFlow(
        this.socialSignInEnabled
          ? 'login:startFullPageSocialSignIn → Firebase redirect (leaving page)'
          : 'login:startFullPageSocialSignIn → loginRedirect invoked (expect leave page)'
      );
      // Browser navigates away to Google / Apple — no return.
    } catch (err: any) {
      logGoogleFlow('login:startFullPageSocialSignIn failed', { err });
      this.isRedirecting = false;
      this.inProgress = false;
      this.isDeepLinkPending = false;
      this.deepLinkSocialProvider = null;
      this.error =
        err?.error?.message ||
        err?.message ||
        `Could not start ${provider === 'google' ? 'Google' : 'Apple'} sign-in. ` +
          (this.socialSignInEnabled
            ? 'Ensure Firebase Google is enabled, firebase is set in environment, and use https://localhost:4200 (npm run start-dv).'
            : 'Ensure https://localhost:4200/redirect is registered in Entra and run npm run cors.');
      this.openModal('login');
      this._cdr.detectChanges();
    }
  }

  /** Full-page loader while redirecting to IdP or finishing OAuth return. */
  private beginSocialOAuthLoader(provider: 'google' | 'apple'): void {
    this.authMode = 'login';
    this.modalStep = 'none';
    this.isDeepLinkPending = true;
    this.deepLinkSocialProvider = provider;
    this.isRedirecting = true;
    this.inProgress = true;
    this.error = '';
    this.sessionReady = true;
  }

  /**
   * After Entra loginRedirect returns, finish Accounts provisioning and
   * bounce back to Lovable with token.
   */
  private async tryCompleteSocialRedirect(): Promise<boolean> {
    const oauthReturn = this.isOAuthRedirectCallback();
    const firebaseReturn = this._authService.isFirebaseSocialRedirectReturn();
    const hadPending = this._authService.hasPendingSocialRedirect();
    logGoogleFlow('login:tryCompleteSocialRedirect:start', {
      oauthReturn,
      firebaseReturn,
      hadPending,
      firebaseMode: this.socialSignInEnabled,
      peek: this._authService.peekSocialDeepLink(),
    });

    if (!this.socialSignInEnabled) {
      if (oauthReturn) {
        await this._authService.ensureMsalRedirectHandled();
      } else {
        await this._authService.ensureMsalReady();
      }

      const sessionEmail = await this._authService.getActiveSessionEmail();
      if (!sessionEmail) {
        logGoogleFlow('login:tryCompleteSocialRedirect:noMsalAccount', {
          oauthReturn,
          hadPending,
        });
        return false;
      }
    }

    let pending = this._authService.peekSocialDeepLink();
    const deepLinkProvider = this.getDeepLinkProvider();
    if (
      !pending.provider &&
      (oauthReturn ||
        firebaseReturn ||
        this._authService.isReturningFromFirebaseSocialRedirect())
    ) {
      pending = {
        provider: deepLinkProvider || pending.provider || 'google',
        returnUrl:
          pending.returnUrl || this._authService.deepLinkReturnUrl,
      };
    }
    if (!pending.provider) {
      logGoogleFlow('login:tryCompleteSocialRedirect:noProvider');
      return false;
    }

    logGoogleFlow('login:tryCompleteSocialRedirect:completeSocialAfterRedirect', {
      pending,
    });

    this.beginSocialOAuthLoader(
      pending.provider || deepLinkProvider || 'google'
    );
    this._cdr.detectChanges();

    return await new Promise<boolean>((resolve) => {
      this._authService.completeSocialAfterRedirect().subscribe({
        next: async (result: ValidOTPResponse | LoginResponse) => {
          logGoogleFlow('login:completeSocialAfterRedirect success', {
            status: result.status,
          });
          this._authService.consumeSocialDeepLink();
          this._authService.clearSocialRedirectAttemptWindow();
          const resultEmail = (result as LoginResponse).email || '';
          if (resultEmail) {
            this.email = resultEmail;
          } else if (!this.email) {
            this.email =
              (await this._authService.getActiveSessionEmail()) || '';
          }
          if (this.handleEntraAuthResult(result)) {
            logGoogleFlow('login:handleEntraAuthResult → post-login redirect');
            resolve(true);
            return;
          }
          logGoogleFlow('login:handleEntraAuthResult failed token handoff');
          this.failSocialDeepLinkReturn(
            'Could not complete social sign-in.',
            pending.returnUrl,
            pending.provider
          );
          resolve(true);
        },
        error: (err: unknown) => {
          const message =
            (err as { error?: { message?: string } })?.error?.message ||
            this._authService.resolveApiError(err) ||
            'Google sign-in did not finish. If you originally signed up with email, use email to sign in instead of Google.';
          console.warn('[auth] completeSocialAfterRedirect failed', err);
          logGoogleFlow('login:completeSocialAfterRedirect error', {
            message,
            err,
          });
          this.failSocialDeepLinkReturn(message, pending.returnUrl, pending.provider);
          resolve(true);
        },
      });
    });
  }

  /** Social redirect failed — show error on dev-auth; keep returnUrl for retry. */
  private failSocialDeepLinkReturn(
    message: string,
    returnUrl: string | null,
    _providerHint: 'google' | 'apple' | null = null
  ): void {
    logGoogleFlow('login:failSocialDeepLinkReturn → stay on modal', {
      message,
      returnUrl,
    });
    if (returnUrl) {
      this._authService.captureDeepLinkReturnUrl(returnUrl);
    }
    this._authService.consumeSocialDeepLink();
    this._authService.clearStaleSocialRedirectPending();
    this.error = message;
    this.sessionReady = true;
    this.isDeepLinkPending = false;
    this.isRedirecting = false;
    this.inProgress = false;
    this.deepLinkSocialProvider = null;
    this.openModal('login');
    this._cdr.detectChanges();
  }

  /**
   * Deep-link: validate ?email=, resolve-email, then Google / Apple / email OTP.
   */
  private beginEmailDeepLinkLoader(rawEmail: string): void {
    this.email = (rawEmail || '').trim();
    this.authMode = 'login';
    this.deepLinkSocialProvider = null;
    this.isDeepLinkPending = false;
    this.emailDeepLinkActive = true;
    this.showInboxWhileSendingCode();
  }

  /** Show email OTP UI immediately; login/signUp sends the code in the background. */
  private showInboxWhileSendingCode(): void {
    this.modalStep = 'code';
    this.otp = '';
    this.error = '';
    this.sessionReady = true;
    this.inProgress = true;
    this._cdr.detectChanges();
  }

  private async startEmailDeepLinkLoginFromRoute(
    rawEmail: string
  ): Promise<boolean> {
    const email = (rawEmail || '').trim();
    if (!email) {
      return false;
    }

    if (!Utils.isValidEmail(email)) {
      this.error = 'Enter a valid email address';
      this.isDeepLinkPending = false;
      this.emailDeepLinkActive = false;
      this.inProgress = false;
      this.openModal('login');
      return true;
    }

    this._authService.releaseSignedOutGateForLogin();
    this.beginEmailDeepLinkLoader(email);
    await this.runContinueWithEmail();
    return true;
  }

  useAnotherAccount() {
    this._authService.clearGooglePasswordlessLinkSession();
    this.passwordlessEmailLinkMode = false;
    this.modalStep = 'email';
    this.email = '';
    this.otp = '';
    this.password = '';
    this.error = '';
    this.secondsCountdown = 0;
    this.inProgress = false;
    this.isRedirecting = false;
    this.emailDeepLinkActive = false;
    this._authService.clearEmailDeepLinkIntent();
  }

  loadSmsMfaStatus() {
    this.smsMfaStatusLoading = true;
    this._authService.getSmsMfaStatus().subscribe({
      next: (status) => {
        this.smsMfaEnabled = !!status.enabled;
        this.smsMfaStatusMessage = status.message || '';
        this.smsMfaStatusLoading = false;
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        this.smsMfaEnabled = false;
        this.smsMfaStatusMessage = '';
        this.error =
          err?.error?.message ||
          'Could not load SMS two-factor authentication status. You can still try enabling it below.';
        this.smsMfaStatusLoading = false;
        this._cdr.detectChanges();
      },
    });
  }

  enableSmsMfa() {
    this.error = '';
    this.enablingSmsMfa = true;
    this.smsMfaPrepareApplied = false;
    this.phoneNumber = '';
    this.otp = '';
    this.partialPhone = '';
    this.isLoggedIn = false;
    this.modalStep = 'phone-register';
    this._cdr.detectChanges();
  }

  cancelSmsMfaEnable() {
    this.error = '';
    const rollback = this.smsMfaPrepareApplied && !this.smsMfaEnabled;
    if (rollback) {
      this.smsMfaActionInProgress = true;
      this._authService.disableSmsMfa().subscribe({
        complete: () => this.resetSmsMfaEnableUi(),
        error: () => this.resetSmsMfaEnableUi(),
      });
      return;
    }
    this.resetSmsMfaEnableUi();
  }

  private resetSmsMfaEnableUi() {
    this._authService.clearSmsMfaEnableFlow();
    this.enablingSmsMfa = false;
    this.smsMfaPrepareApplied = false;
    this.phoneNumber = '';
    this.otp = '';
    this.partialPhone = '';
    this.modalStep = 'none';
    this.smsMfaActionInProgress = false;
    this.inProgress = false;
    this.isLoggedIn = true;
    this.loadSmsMfaStatus();
    this._cdr.detectChanges();
  }

  private finishSmsMfaEnable() {
    this._authService.clearSmsMfaEnableFlow();
    this.enablingSmsMfa = false;
    this.smsMfaPrepareApplied = false;
    this.smsMfaEnabled = true;
    this.smsMfaStatusMessage =
      'SMS two-factor authentication is enabled for this account.';
    this.phoneNumber = '';
    this.otp = '';
    this.partialPhone = '';
    this.modalStep = 'none';
    this.inProgress = false;
    this.smsMfaActionInProgress = false;
    this.isLoggedIn = true;
    this.loadSmsMfaStatus();
    this._cdr.detectChanges();
  }

  private startSmsMfaEnableWithPhone(phone: string) {
    this.inProgress = true;
    this.smsMfaActionInProgress = true;
    this._authService.startSmsMfaEnableFlow(this.email, phone).subscribe({
      next: (response: LoginResponse | ValidOTPResponse) => {
        this.smsMfaPrepareApplied = true;
        if (!this.handleEntraAuthResult(response)) {
          this.handleLoginChallenge(response as LoginResponse);
        }
      },
      error: (err: any) => {
        this.error =
          err?.error?.message ||
          'Could not start SMS two-factor authentication setup.';
        this._authService.clearSmsMfaEnableFlow();
        this.enablingSmsMfa = false;
        this.smsMfaPrepareApplied = false;
        this.isLoggedIn = true;
        this.modalStep = 'none';
        this._authService.disableSmsMfa().subscribe({
          complete: () => this.loadSmsMfaStatus(),
        });
      },
    }).add(() => {
      this.inProgress = false;
      this.smsMfaActionInProgress = false;
      this._cdr.detectChanges();
    });
  }

  disableSmsMfa() {
    this.error = '';
    this.smsMfaActionInProgress = true;
    this._authService.disableSmsMfa().subscribe({
      next: (status) => {
        this.smsMfaEnabled = false;
        this.smsMfaStatusMessage = status.message || '';
        this.smsMfaActionInProgress = false;
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        this.error = err?.error?.message || 'Could not disable SMS 2FA.';
        this.smsMfaActionInProgress = false;
        this._cdr.detectChanges();
      },
    });
  }

  onSocialClick(provider: 'google' | 'apple') {
    if (!isSocialSignInEnabled()) {
      return;
    }
    logGoogleFlow('login:onSocialClick (modal button)', { provider });
    void this.startFullPageSocialSignIn(provider);
  }

  retryDeepLinkSocialRedirect() {
    if (!isSocialSignInEnabled()) {
      this.openModal('login');
      return;
    }
    const provider = this.deepLinkSocialProvider;
    if (!provider) {
      return;
    }
    if (!this._authService.canAutoStartSocialRedirect()) {
      this.error =
        'Please wait a moment before trying Google sign-in again.';
      this._cdr.detectChanges();
      return;
    }
    void this.tryDeepLinkSocialLogin(provider);
  }

  private emailFromIdToken(token: string | undefined): string {
    if (!token) {
      return '';
    }
    try {
      const claims = Utils.parseJwt(token);
      return (
        (claims?.email || claims?.preferred_username || claims?.upn || '')
          .toString()
          .trim()
          .toLowerCase() || ''
      );
    } catch {
      return '';
    }
  }

  onSubmit() {
    if (this.modalStep === 'password') {
      this.verifyPassword();
      return;
    }
    if (this.modalStep === 'code') {
    this.verifyOTP();
      return;
    }
    if (this.modalStep === 'email-link-confirm') {
      void this.completeEmailLinkWithConfirmedEmail();
      return;
    }
    this.validateEmail();
  }

  /** Cross-browser / cross-device: confirm email when localStorage has no stored address. */
  async completeEmailLinkWithConfirmedEmail(): Promise<void> {
    this.error = '';
    if (!Utils.isValidEmail(this.email)) {
      this.error = 'Enter the email address where the sign-in link was sent.';
      return;
    }

    this.inProgress = true;
    this._cdr.detectChanges();
    try {
      const linked = await this._authService.tryCompleteFirebaseEmailLinkSignIn(
        this.email
      );
      if (linked?.status === 'Token') {
        this.handleEntraAuthResult(linked);
        return;
      }
      this.error = 'Could not complete sign-in. Request a new link and try again.';
    } catch (err: unknown) {
      this.applyAuthApiError(err);
    } finally {
      this.inProgress = false;
      this._cdr.detectChanges();
    }
  }

  validateEmail() {
    this.error = '';
    if (!this.loginForm) {
      return;
    }
    this.loginForm.form.markAllAsTouched();
    if (this.loginForm?.invalid) {
      return;
    }
    this.continueWithEmail();
  }

  continueWithEmail() {
    if (this.secondsCountdown > 0 && this.modalStep === 'code') {
      return;
    }
    if (this.modalStep === 'code') {
      this.resendCode();
      return;
    }
    void this.runContinueWithEmail();
  }

  /** POST resolve-email, then social redirect or Entra email OTP initiate. */
  private async runContinueWithEmail(): Promise<void> {
    this.otp = '';
    this.password = '';
    this.error = '';
    this.inProgress = true;
    this._cdr.detectChanges();

    // Google email OTP: show the inbox/code step immediately while the BFF sends OTP.
    if (useGoogleEmailOtpIdp() && this.modalStep !== 'code') {
      this.showInboxWhileSendingCode();
    }

    try {
      if (await this.tryRouteLoginBySignUpAuthMethod()) {
        return;
      }
    } catch (err: unknown) {
      console.warn('[auth] resolve-email routing failed; falling back to OTP', err);
    }

    if (this.modalStep !== 'code') {
      this.showInboxWhileSendingCode();
    }

    const request$ =
      this.authMode === 'signup'
        ? this._authService.signUp(this.email)
        : this._authService.login(this.email);

    request$
      .subscribe({
        next: (response: LoginResponse | ValidOTPResponse) => {
          this._authService.clearEmailDeepLinkIntent();
          this.authMode = this._authService.effectiveAuthMode;
          this.isDeepLinkPending = false;
          this.emailDeepLinkActive = false;
          if (!this.handleEntraAuthResult(response)) {
            this.handleLoginChallenge(response as LoginResponse);
          }
        },
        error: (err: any) => {
          this.applyAuthApiError(err);
          this.isDeepLinkPending = false;
          this.emailDeepLinkActive = false;
          if (this.modalStep !== 'code') {
            this.openModal('login');
            if (this.email) {
              this.modalStep = 'email';
            }
          }
        },
      })
      .add(() => {
        if (!this.isRedirecting) {
          this.inProgress = false;
        }
        this._cdr.detectChanges();
      });
  }

  /**
   * If Accounts has signUpAuthMethod google|apple, start Continue with Google/Apple.
   */
  private async tryRouteLoginBySignUpAuthMethod(): Promise<boolean> {
    if (useGoogleEmailOtpIdp()) {
      return false;
    }
    if (!isSocialSignInEnabled()) {
      return false;
    }
    if (!this._authService.shouldRouteEmailBySignUpAuthMethod()) {
      return false;
    }
    const email = (this.email || '').trim();
    if (!Utils.isValidEmail(email)) {
      return false;
    }

    const link = await this._authService.resolveEmailAccountsLink(email);
    if (link.signUpAuthMethod === 'google') {
      await this.startFullPageSocialSignIn('google', {
        deepLinkUi: this.isDeepLinkPending || this.emailDeepLinkActive,
      });
      return true;
    }
    if (link.signUpAuthMethod === 'apple') {
      await this.startFullPageSocialSignIn('apple', {
        deepLinkUi: this.isDeepLinkPending || this.emailDeepLinkActive,
      });
      return true;
    }
    return false;
  }

  resendCode() {
    if (this.secondsCountdown > 0) {
      return;
    }
    this.error = '';
    this.inProgress = true;
    this._authService
      .resendLoginCode()
      .subscribe({
        next: () => {
            this.secondsCountdown = 60;
            this.startTimer();
        },
        error: (err: any) => {
          this.applyAuthApiError(err);
        },
      })
      .add(() => {
        this.inProgress = false;
      });
  }

  loginWithEmailLink() {
    this.error = '';
    if (!Utils.isValidEmail(this.email)) {
      this.error = 'Enter a valid email address first.';
      return;
    }
    this.inProgress = true;
    this._authService.switchToGooglePasswordlessEmailLink(this.email).subscribe({
      next: () => {
        this.passwordlessEmailLinkMode = true;
        this.otp = '';
        this.inProgress = false;
        this.secondsCountdown = 60;
        this.startTimer();
        this._cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.applyAuthApiError(err);
        this.inProgress = false;
        this._cdr.detectChanges();
      },
    });
  }

  verifyPassword() {
    this.error = '';
    if (!this.password) {
      this.error = 'Please enter your password.';
      return;
    }
    this.inProgress = true;
    this._authService
      .submitPassword(
        this.password,
        this.email,
        this._authService.effectiveAuthMode || this.authMode
      )
      .subscribe({
        next: (result: ValidOTPResponse | LoginResponse) => {
          if (
            'status' in result &&
            (result.status === 'SMS' ||
              result.status === 'Password' ||
              result.status === 'Authenticator' ||
              result.status === 'Completed')
          ) {
            const loginResult = result as LoginResponse;
            if (loginResult.status === 'Completed' && loginResult.token) {
              this.saveAuthDataAndRedirect(this.email, loginResult.token);
              return;
            }
            if (
              loginResult.status === 'SMS' ||
              loginResult.status === 'Authenticator'
            ) {
              this.handleLoginChallenge(loginResult);
              this.inProgress = false;
              return;
            }
          }
          if (this.handleEntraAuthResult(result)) {
            this.inProgress = false;
            return;
          }
          this.inProgress = false;
        },
        error: (err: any) => {
          this.applyAuthApiError(err);
          this.inProgress = false;
        },
      });
  }

  verifyOTP() {
    this.error = '';
    if (!this.otp) {
      this.error = 'Please input verification code.';
      return;
    }
    this.inProgress = true;
    this._authService.validateOTP(this.otp, this.email).subscribe({
      next: (result: ValidOTPResponse | LoginResponse) => {
        if (this.handleEntraAuthResult(result)) {
          if (!this.isRedirecting) {
          this.inProgress = false;
          }
        } else {
          this.inProgress = false;
        }
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        if (this._authService.isGeofenceDeniedError(err)) {
          this.showGeofenceBlocked();
          this.inProgress = false;
          return;
        }
        if (err.error?.mfaRequired) {
          this.error = err.error?.message || '';
          this.otp = '';
          this.secondsCountdown = 60;
          this.startTimer();
        } else if (err.error?.messages?.length) {
          this.error = 'Invalid code or code expired.';
        } else {
          this.error = err.error?.message || '';
        }
        this.inProgress = false;
      },
    });
  }

  private registerPendingSmsMfaPhone() {
    const phone = this._authService.pendingSmsMfaEnablePhone;
    if (!phone) {
      return;
    }
    this.inProgress = true;
    this._authService.registerStrongAuthPhone(phone, this.email).subscribe({
      next: (result: ValidOTPResponse | LoginResponse) => {
        if (this.handleEntraAuthResult(result)) {
          this.inProgress = false;
          this._cdr.detectChanges();
          return;
        }
        this.inProgress = false;
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        this.error = err.error?.message || 'Could not send SMS code.';
        this.inProgress = false;
        this._cdr.detectChanges();
      },
    });
  }

  submitPhoneRegistration() {
    this.error = '';
    const phone = Utils.normalizePhoneForEntra(this.phoneNumber.trim());
    if (!phone) {
      this.error = 'Enter your mobile number including country code (e.g. +92 3155432768).';
      return;
    }
    this.phoneNumber = phone;
    if (this.enablingSmsMfa) {
      this.startSmsMfaEnableWithPhone(phone);
      return;
    }
    this.inProgress = true;
    this._authService.registerStrongAuthPhone(phone, this.email).subscribe({
      next: (result: ValidOTPResponse | LoginResponse) => {
        if (this.handleEntraAuthResult(result)) {
          this.inProgress = false;
          this._cdr.detectChanges();
          return;
        }
        this.inProgress = false;
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        this.error = err.error?.message || 'Could not send SMS code.';
        this.inProgress = false;
        this._cdr.detectChanges();
      },
    });
  }

  private handleEntraAuthResult(
    result: ValidOTPResponse | LoginResponse
  ): boolean {
    if (this._authService.wasSignedOutLocally()) {
      return false;
    }
    if (this.isLoginChallenge(result)) {
      if (result.status === 'Completed' && !(result as LoginResponse).token) {
        this.isRedirecting = true;
        this.inProgress = true;
        this.handleLoginChallenge(result);
        return true;
      }
      this.handleLoginChallenge(result);
      return true;
    }

    const otpResult = result as ValidOTPResponse;
    if (otpResult.status === 'Token') {
      if (this.enablingSmsMfa || this._authService.smsMfaEnableInProgress) {
        this.finishSmsMfaEnable();
        return true;
      }
      this.saveAuthDataAndRedirect(this.email, <AuthToken>otpResult.response);
      return true;
    }

    if (otpResult.status === 'Companies') {
      const postAuthApp = this.resolvePostAuthAppQuery();
      if (
        postAuthApp === AvailableApplication.Admin &&
        otpResult.tempAccessToken?.trim()
      ) {
        this.isRedirecting = true;
        this.inProgress = true;
        const token: AuthToken = {
          accessToken: otpResult.tempAccessToken.trim(),
          refreshToken: '',
        };
        this._authService.persistAuthData(this.email, token);
        this._authService.redirectToPostAuthApp(AvailableApplication.Admin);
        return true;
      }
      this.isRedirecting = true;
      this.inProgress = true;
      this._authService.userCompanies = <UserCompany[]>otpResult.response;
      this._authService.accessToken = otpResult.tempAccessToken || '';
      this._router.navigate(['select-company'], {
        replaceUrl: true,
        queryParams: {
          app: postAuthApp,
          email: this.email,
          ...(this._authService.deepLinkReturnUrl
            ? { returnUrl: this._authService.deepLinkReturnUrl }
            : {}),
        },
      });
      return true;
    }

    return false;
  }

  private isLoginChallenge(
    result: ValidOTPResponse | LoginResponse
  ): result is LoginResponse {
    return (
      result.status === 'SMS' ||
      result.status === 'Password' ||
      result.status === 'Authenticator' ||
      result.status === 'Completed' ||
      result.status === 'PhoneRegistration' ||
      result.status === 'MfaCode'
    );
  }

  saveAuthDataAndRedirect(userName: string, token: AuthToken) {
    this._authService.persistAuthData(userName, token);

    if (this.isSmsMfaManageDeepLink()) {
      this.isLoggedIn = true;
      this.email = userName;
      this.modalStep = 'none';
      void this.loadDisplayName(userName);
      this.loadSmsMfaStatus();
      this._cdr.detectChanges();
      return;
    }

    this.isRedirecting = true;
    this.inProgress = true;
    this._cdr.detectChanges();
    void this.finishAuthRedirectAfterEntraSession(userName, token);
  }

  /** Email/native session established — optional Google link, then return to Lovable. */
  private async finishAuthRedirectAfterEntraSession(
    userName: string,
    token: AuthToken
  ): Promise<void> {
    token = await this._authService.hydrateAuthTokenRefreshToken(userName, token);

    const pendingGoogle = this._authService.hasPendingGoogleLink();
    const resolvedReturnUrl = this.resolvePostAuthReturnUrl();
    const appQueryParam = this.resolvePostAuthAppQuery();
    if (this._authService.redirectsWithoutProductLicense(appQueryParam)) {
      this._authService.prepareProductAppLoginEntry(appQueryParam);
    }

    logGoogleFlow('login:finishAuthRedirectAfterEntraSession:start', {
      userName,
      pendingGoogle,
      deepLinkReturnUrl: this._authService.deepLinkReturnUrl,
      resolvedReturnUrl,
      appQuery: appQueryParam,
    });

    if (pendingGoogle) {
      this.inProgress = true;
      this.error = '';
      this._cdr.detectChanges();
      const linked = await this._authService.tryCompletePendingGoogleLink();
      this.inProgress = false;
      if (linked && this.getDeepLinkProvider() === 'google') {
        this.isRedirecting = true;
        this._cdr.detectChanges();
        await this.tryDeepLinkSocialLogin('google');
        return;
      }
      if (!linked && pendingGoogle) {
        this.error =
          'Sign-in succeeded. Link Google from your profile, or try Continue with Google again after linking.';
        this.openModal('login');
        this._cdr.detectChanges();
      }
    }

    this.isRedirecting = true;
    this.sessionReady = true;
    this._cdr.detectChanges();

    if (appQueryParam) {
      logGoogleFlow('login:finishAuthRedirect → appBasedRedirection', {
        app: appQueryParam,
      });
      this._authService.getProductNamesWithLicenses().subscribe({
        next: (availableProducts) => {
          this._authService.appBasedRedirection(
            availableProducts,
            token,
            appQueryParam
          );
        },
        error: () => {
          if (this._authService.tryPostAuthAppFallback(token, appQueryParam)) {
            return;
          }
          this._authService.redirectToAyaApp();
        },
      });
      return;
    }

    if (this._authService.deepLinkReturnUrl) {
      const url = this._authService.deepLinkReturnUrl;
      this._authService.deepLinkReturnUrl = null;
      logGoogleFlowRedirect(
        'login:finishAuthRedirect → deepLinkReturnUrl handoff',
        url
      );
      try {
        await this._authService.redirectToUrlWithTokenPrepared(url);
      } catch (err: unknown) {
        this.handleHandoffFailure(err);
      }
      return;
    }

    if (resolvedReturnUrl) {
      logGoogleFlowRedirect(
        'login:finishAuthRedirect → resolvedReturnUrl handoff',
        resolvedReturnUrl
      );
      try {
        await this._authService.redirectToUrlWithTokenPrepared(resolvedReturnUrl);
      } catch (err: unknown) {
        this.handleHandoffFailure(err);
      }
      return;
    }

    logGoogleFlow('login:finishAuthRedirect → redirectToAyaApp (default)');
    this._authService.redirectToAyaApp();
  }

  private applyAuthApiError(err: unknown): void {
    if (this._authService.isGeofenceDeniedError(err)) {
      this.showGeofenceBlocked();
      return;
    }
    this.error = this._authService.resolveApiError(err);
  }

  private showGeofenceBlocked(): void {
    this.geofenceBlocked = true;
    this.inProgress = false;
    this.isRedirecting = false;
    this.error = '';
    this.sessionReady = true;
    this._cdr.detectChanges();
  }

  private handleHandoffFailure(err: unknown): void {
    if (this._authService.isGeofenceDeniedError(err)) {
      this.showGeofenceBlocked();
      return;
    }
    const message =
      (err as { error?: { message?: string } })?.error?.message ||
      'Could not complete sign-in handoff. Please try again.';
    this.isRedirecting = false;
    this.inProgress = false;
    this.error = message;
    this.openModal('login');
    this._cdr.detectChanges();
  }

  private handleLoginChallenge(response: LoginResponse) {
    if (
      response.status === 'SMS' ||
      response.status === 'Authenticator' ||
      response.status === 'Password'
    ) {
      this.authType = response.status;
    }
    this.email = response.email || this.email;
    this.partialPhone = response.phone;
    this.isDeepLinkPending = false;
    this.emailDeepLinkActive = false;

    if (response.status === 'Completed' && response.token) {
      this.saveAuthDataAndRedirect(this.email, response.token);
      return;
    }

    if (response.status === 'Completed') {
      this.isRedirecting = true;
      this.inProgress = true;
      return;
    }

    if (response.status === 'Password') {
      this.modalStep = 'password';
      return;
    }

    if (response.status === 'PhoneRegistration') {
      if (
        this.enablingSmsMfa &&
        this._authService.pendingSmsMfaEnablePhone
      ) {
        this.registerPendingSmsMfaPhone();
        return;
      }
      this.modalStep = 'phone-register';
      this.phoneNumber = '';
      return;
    }

    if (response.status === 'MfaCode') {
      this.modalStep = 'mfa-code';
    this.otp = '';
      this.inProgress = false;
      this.secondsCountdown = 60;
      this.startTimer();
      return;
    }

    this.modalStep = 'code';
    this.inProgress = false;
    if (this.authType === 'SMS') {
      this.secondsCountdown = 60;
      this.startTimer();
    }
  }

  private startTimer() {
    this.countdownSubscription.unsubscribe();
    this.countdownSubscription = timer(0, 1000).subscribe(() => {
      --this.secondsCountdown;
      if (this.secondsCountdown === 0) {
        this.countdownSubscription.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.countdownSubscription.unsubscribe();
  }
}
